import { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import ObjectList from "./ObjectList";
import { RoleContext } from "../context/RoleContext";
import DfnsService from "../services/DfnsService";
import { NomyxAction } from "../utils/Constants";

const ClaimTopicsPage = ({ service }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("q") || "";
  const [claimTopics, setClaimTopics] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const { user, dfnsToken } = useContext(RoleContext);

  // A claim topic just created by CreateClaimTopic, handed over via navigation state.
  // Captured once into a ref so it survives re-renders and we can clear the router state.
  const pendingTopicRef = useRef(location.state?.newTopic || null);

  const columns = [
    { label: "Id", name: "attributes.topic" },
    { label: "Compliance Rule", name: "attributes.displayName", width: "95%" },
  ];

  const actions = [
    { label: "View", name: NomyxAction.ViewClaimTopic },
    { label: "Edit", name: NomyxAction.EditClaimTopic },
    {
      label: "Remove",
      name: NomyxAction.RemoveClaimTopic,
      confirmation: "Are you sure you want to remove this Compliance Rule?",
    },
  ];

  const globalActions = [{ label: "Create Compliance Rule", name: NomyxAction.CreateClaimTopic }];

  const search = true;

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    if (error?.reason) return error.reason;
    if (error?.message) return error.message;
    if (error?.toString) return error.toString();
    return "An unknown error occurred";
  };

  const triggerRefresh = () => {
    setRefreshTrigger((prev) => !prev);
  };

  const checkClaimTopicRemoved = async (topic, maxAttempts = 10, intervalMs = 1000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const topics = await service.getClaimTopics();
        const stillActive = topics?.some((t) => String(t.attributes.topic) === String(topic));

        if (!stillActive) {
          return true;
        }
      } catch (error) {
        console.error(`Error checking compliance rule removal status (attempt ${attempt + 1}):`, error);
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    }

    return false;
  };

  const removeClaimTopic = async (record) => {
    const topic = record?.attributes?.topic;

    if (!topic) {
      toast.error("This compliance rule is missing its topic id and cannot be removed.");
      return;
    }

    try {
      await toast.promise(
        (async () => {
          const { initiateResponse, error: initError } = await DfnsService.initiateRemoveClaimTopic(Number(topic), user.walletId, dfnsToken);
          if (initError) throw new Error(initError);

          const { completeResponse, error: completeError } = await DfnsService.completeRemoveClaimTopic(
            user.walletId,
            dfnsToken,
            initiateResponse.challenge,
            initiateResponse.requestBody
          );
          if (completeError) throw new Error(completeError);

          const isRemoved = await checkClaimTopicRemoved(topic);

          if (!isRemoved) {
            throw new Error("Transaction completed but the compliance rule is still listed. Please refresh manually.");
          }

          triggerRefresh();

          return completeResponse;
        })(),
        {
          pending: "Removing Compliance Rule from blockchain and database...",
          success: "Compliance Rule has been successfully removed",
          error: {
            render({ data }) {
              const errorMessage = getErrorMessage(data);
              return <div>{errorMessage || "An error occurred while removing this Compliance Rule"}</div>;
            },
          },
        }
      );
    } catch (error) {
      console.error("Unexpected error during removeClaimTopic:", error);
      toast.error("We couldn't remove this compliance rule. Please try again.");
    }
  };

  const handleAction = async (event, action, record) => {
    switch (action) {
      case NomyxAction.CreateClaimTopic:
        navigate("/topics/create");
        break;
      case NomyxAction.ViewClaimTopic:

        // Optimistic placeholder rows have no real objectId yet — ignore clicks until reconciled.
        if (record?.__optimistic) return;

        navigate("/topics/" + record.id);
        break;
      case NomyxAction.EditClaimTopic:
        navigate("/topics/edit/" + record.id);
        break;
      case NomyxAction.RemoveClaimTopic:
        await removeClaimTopic(record);
        break;
      default:
        console.log("Unknown action: " + action);
    }
  };

  // Clear the one-shot navigation state so a manual refresh doesn't re-insert the optimistic row.
  useEffect(() => {
    if (location.state?.newTopic) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // run once on mount; pendingTopicRef already captured the value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8; // ~32s of reconciliation at the interval below
    const INTERVAL_MS = 4000;
    let timer;

    // Merge the indexed records with the pending optimistic topic (2c).
    // Once the real record shows up, drop the optimistic placeholder and stop tracking it.
    const buildList = (records) => {
      const list = Array.isArray(records) ? records : [];
      const pending = pendingTopicRef.current;
      if (!pending) return list;

      const exists = list.some((r) => String(r?.attributes?.topic) === String(pending.topic));
      if (exists) {
        pendingTopicRef.current = null; // reconciled with indexed data
        return list;
      }

      const optimistic = {
        id: `optimistic-${pending.topic}`,
        attributes: { topic: String(pending.topic), displayName: pending.displayName },
        __optimistic: true,
      };
      return [optimistic, ...list];
    };

    const load = async () => {
      try {
        const result = await service.getClaimTopics();
        if (cancelled) return;
        setClaimTopics(buildList(result));
      } catch (e) {
        if (!cancelled) console.error("Failed to load claim topics:", e);
      }
    };

    // Initial fetch always runs.
    void load();

    // Only poll while we have a freshly created topic awaiting indexing (2a).
    if (pendingTopicRef.current) {
      timer = setInterval(async () => {
        attempts += 1;
        await load();
        if (cancelled) return;
        if (!pendingTopicRef.current || attempts >= MAX_ATTEMPTS) {
          clearInterval(timer);
        }
      }, INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [service, refreshTrigger]);

  return (
    <ObjectList
      title="Compliance Rules"
      description="Compliance Rules describe the types of Claims that can be created for any Identity"
      columns={columns}
      actions={actions}
      globalActions={globalActions}
      search={search}
      searchTerm={initialSearch}
      data={claimTopics}
      pageSize={10}
      onAction={handleAction}
      onGlobalAction={handleAction}
    />
  );
};

export default ClaimTopicsPage;
